<template>
  <transition name="fade">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" @click.self="$emit('close')">
      <div class="flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center">
        <div class="fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50" @click="$emit('close')"></div>
        <div class="inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-2xl sm:w-full flex flex-col">
          <div class="bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center gap-3 min-w-0">
              <div class="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <Link2
				v-if="material?.type === 'URL Link'"
				class="h-5 w-5 text-white"
				/>

				<FileText
				v-else-if="material?.type === 'Doc File'"
				class="h-5 w-5 text-white"
				/>
              </div>
              <div class="min-w-0">
                <h3 class="text-lg font-bold text-white truncate">{{ material?.title || 'Material Details' }}</h3>
                <p class="text-sm text-white/70 truncate">{{ material?.type }}</p>
              </div>
            </div>
            <button @click="$emit('close')" class="shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors">
              <X class="h-5 w-5" />
            </button>
          </div>
          <div class="bg-white dark:bg-gray-800 px-3 pt-4 pb-3 sm:p-6 sm:pb-4 overflow-y-auto flex-1 max-h-[calc(100vh-12rem)]">
            <!-- Basic Information -->
            <div class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 mb-5">
              <h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
				<BookCopy class="h-4 w-4 inline" />
				Basic Information
			</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="md:col-span-2">
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Title</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 mt-1">{{ material?.title || '—' }}</p>
                </div>
                <div class="md:col-span-2">
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Description</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 mt-1">{{ material?.description || 'No description' }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Type</p>
                  <span class="inline-flex items-center gap-1 mt-1 px-2.5 py-1 text-xs font-medium rounded-lg"
                    :class="typeBadgeClass(material?.type)"
                  >
                    <FileText v-if="material?.type === 'Doc File'" class="h-5 w-4" />
                    <Globe v-else-if="material?.type === 'Online form'" class="h-4 w-4" />
                    <Link2 v-else-if="material?.type === 'URL Link'" class="h-4 w-4" />
                    {{ material?.type || '—' }}
                  </span>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Status</p>
                  <span class="inline-flex items-center mt-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full"
                    :class="material?.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'"
                  >
                    {{ material?.status === 'active' ? 'Active' : 'Inactive' }}
                  </span>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Date Added</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 mt-1">
                    {{ material?.created_at ? new Date(material.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—' }}
                  </p>
                </div>
              </div>
            </div>
            <!-- Viewers -->
            <div v-if="!isEmployee" class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 mb-5">
              <h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
				<ScanEye class="h-4 w-4 inline" />
				Viewers & Access
			</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">By Position</p>
                  <div class="mt-1.5">
                    <template v-if="material?.position_viewers && material.position_viewers.length > 0">
                      <span v-for="(v) in material.position_viewers" :key="'pos-'+v.id"
                        class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark mr-1.5 mb-1.5"
                      >{{ v.name }}</span>
                    </template>
                    <p v-else class="text-sm text-gray-400 italic">None</p>
                  </div>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">By Employee</p>
                  <div class="mt-1.5">
                    <template v-if="material?.user_viewers && material.user_viewers.length > 0">
                      <span v-for="(v) in material.user_viewers" :key="'usr-'+v.id"
                        class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark mr-1.5 mb-1.5"
                      >{{ v.first_name ? v.first_name + ' ' + v.last_name : v.username }}</span>
                    </template>
                    <p v-else class="text-sm text-gray-400 italic">None</p>
                  </div>
                </div>
              </div>
            </div>
            <!-- File / Link Preview -->
            <div v-if="material?.type === 'Doc File'" class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 mb-5">
              <h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
				<FolderOpen class="h-4 w-4 inline" />
				File Preview
			</h4>
              <div v-if="material?.file_path">
                <div v-if="isPreviewableImage(material.file_path)" class="flex items-center justify-center bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
                  <img :src="materialService.getFileUrl(material.file_path)" alt="file preview" class="max-w-full max-h-[50vh] rounded object-contain" />
                </div>
                <div v-else-if="isPreviewablePdf(material.file_path)" class="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                  <iframe :src="materialService.getFileUrl(material.file_path)" class="w-full h-[50vh]"></iframe>
                </div>
                <div v-else class="text-center py-8 text-gray-500 border border-dashed rounded-xl">
                  <FileText class="h-12 w-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                  <p class="text-base font-medium">Preview not available</p>
                  <p class="text-sm mt-1">This file type cannot be previewed in the browser.</p>
                </div>
              </div>
              <p v-else class="text-sm text-gray-400 italic">No file attached.</p>
            </div>
            <div v-else-if="material?.type === 'URL Link' || material?.type === 'Online form'" class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 mb-5">
              <h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
				<Link class="h-4 w-4 inline" />
				Link
			</h4>
              <div v-if="material?.url_link">
                <div class="flex items-center justify-between gap-3">
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">URL</p>
                  <a :href="material.url_link" target="_blank"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-primary hover:bg-secondary shadow-sm transition-all shrink-0">
                    Open <ExternalLink class="h-3.5 w-3.5" />
                  </a>
                </div>
                <p class="text-sm font-medium text-blue-600 dark:text-blue-400 break-all mt-2">{{ material.url_link }}</p>
              </div>
              <p v-else class="text-sm text-gray-400 italic">No link provided.</p>
            </div>
          </div>
          <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0 shrink-0">
            <button
              v-if="material?.type === 'Doc File' && material?.file_path"
              @click="handleDownload"
              class="w-full sm:w-auto sm:ml-3 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-all inline-flex items-center justify-center gap-2"
            >
              <DownloadIcon class="h-4 w-4" />
              Download
            </button>
            <button
              type="button"
              class="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors"
              @click="$emit('close')"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import {
	FileText, Link2, ExternalLink,
	X, CloudDownload as DownloadIcon,
	BookCopy, ScanEye, Link, FolderOpen
} from 'lucide-vue-next'
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { materialService } from '@/services/material.service'
import type { Material } from '@/config/types/material'
import { useToast } from 'vue-toastification'

const toast = useToast()
const toastTimeout = 5000

const authStore = useAuthStore()

const isEmployee = computed(() => authStore.currentUser?.role?.toLowerCase() === 'employee')

const props = defineProps<{
  isOpen: boolean
  material: Material | null
}>()

defineEmits<{
  (e: 'close'): void
}>()

const typeBadgeClass = (type?: string) => {
  switch (type) {
    case 'Doc File': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
    case 'Online form': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
    case 'URL Link': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400'
    default: return 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
  }
}

const isPreviewableImage = (filePath: string) => {
  const ext = filePath.toLowerCase().split('.').pop()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')
}

const isPreviewablePdf = (filePath: string) => {
  return filePath.toLowerCase().endsWith('.pdf')
}

const handleDownload = async () => {
  if (!props.material?.id) return
  try {
    const token = localStorage.getItem('token')
    const url = materialService.getDownloadUrl(props.material.id)
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Invalid File')
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = props.material.title || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(downloadUrl)
    document.body.removeChild(a)
    toast.success('Document downloaded successfully', { timeout: toastTimeout })
  } catch (err: any) {
    toast.error(err.message || 'Failed to download file', { timeout: toastTimeout })
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
